import React, { useState } from 'react';

import { Alert, Button, Container } from '@meu-mapa-nev/design-system';

import type { TrustedDevice } from './api.js';

export interface TelaDeDispositivosProps {
  readonly devices: readonly TrustedDevice[];
  /** Solicita a reautenticação necessária antes de revogar. */
  readonly onRequestReauthentication: () => void;
  readonly onRevokeOne: (deviceId: string) => void;
  readonly onRevokeAll: () => void;
  /** Prova de reautenticação válida no momento, quando houver. */
  readonly hasReauthentication: boolean;
  readonly submitting?: boolean;
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Gestão de dispositivos confiáveis (ADR-0010, decisão D3).
 *
 * Cada elemento desta tela corresponde a um requisito das fontes:
 *
 * - a lista mostra a data de expiração, para que nenhum aparelho fique
 *   confiável por esquecimento;
 * - a revogação individual e a global existem lado a lado;
 * - ambas exigem reautenticação recente, pedida antes de qualquer ação;
 * - nenhum segredo aparece: só rótulo, datas e se é o aparelho em uso.
 *
 * O aviso sobre os 30 dias é explícito porque o prazo é o teto das fontes e a
 * recomendação técnica, e a contenção do risco passou a depender de a
 * administradora perceber um aparelho que não reconhece.
 */
export const TelaDeDispositivos: React.FC<TelaDeDispositivosProps> = ({
  devices,
  onRequestReauthentication,
  onRevokeOne,
  onRevokeAll,
  hasReauthentication,
  submitting = false,
}) => {
  const [confirmandoTudo, setConfirmandoTudo] = useState(false);

  return (
    <Container>
      <h2
        style={{
          fontFamily: 'var(--nev-font-heading)',
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'var(--nev-text-primary)',
          marginBottom: 'var(--nev-space-3)',
        }}
      >
        Dispositivos confiáveis
      </h2>

      <p
        style={{
          fontFamily: 'var(--nev-font-body)',
          fontSize: '0.9375rem',
          lineHeight: 1.6,
          color: 'var(--nev-text-secondary)',
          marginBottom: 'var(--nev-space-4)',
        }}
      >
        Um dispositivo confiável dispensa o código de confirmação por 30 dias. Se você não
        reconhecer algum aparelho desta lista, revogue-o.
      </p>

      {!hasReauthentication && (
        <Alert type="warning" title="Confirmação de identidade necessária">
          Para revogar dispositivos, confirme sua identidade agora. A confirmação vale por 5
          minutos.
          <div style={{ marginTop: 'var(--nev-space-3)' }}>
            <Button variant="primary" onClick={onRequestReauthentication} disabled={submitting}>
              Confirmar identidade
            </Button>
          </div>
        </Alert>
      )}

      {devices.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--nev-font-body)',
            color: 'var(--nev-text-secondary)',
            padding: 'var(--nev-space-4)',
            border: '1px dashed var(--nev-border-subtle)',
            borderRadius: 'var(--nev-radius-control)',
          }}
        >
          Nenhum dispositivo confiável cadastrado. O código de confirmação será pedido em toda
          entrada.
        </p>
      ) : (
        <>
          {/*
            Tabela no desktop; em telas estreitas cada linha vira um bloco
            empilhado, mantendo a ação essencial disponível no celular.
          */}
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: 'var(--nev-font-body)',
                fontSize: '0.9375rem',
              }}
            >
              <caption
                style={{
                  textAlign: 'left',
                  paddingBottom: 'var(--nev-space-2)',
                  color: 'var(--nev-text-secondary)',
                  fontSize: '0.8125rem',
                }}
              >
                {devices.length === 1
                  ? '1 dispositivo confiável ativo'
                  : `${String(devices.length)} dispositivos confiáveis ativos`}
              </caption>
              <thead>
                <tr>
                  <th scope="col" style={{ textAlign: 'left', padding: 'var(--nev-space-2)' }}>
                    Aparelho
                  </th>
                  <th scope="col" style={{ textAlign: 'left', padding: 'var(--nev-space-2)' }}>
                    Último uso
                  </th>
                  <th scope="col" style={{ textAlign: 'left', padding: 'var(--nev-space-2)' }}>
                    Expira em
                  </th>
                  <th scope="col" style={{ textAlign: 'left', padding: 'var(--nev-space-2)' }}>
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.id} style={{ borderTop: '1px solid var(--nev-border-subtle)' }}>
                    <th
                      scope="row"
                      style={{
                        textAlign: 'left',
                        padding: 'var(--nev-space-2)',
                        fontWeight: 500,
                      }}
                    >
                      {device.label}
                      {device.current && (
                        <span
                          style={{
                            marginLeft: 'var(--nev-space-2)',
                            fontSize: '0.75rem',
                            color: 'var(--nev-text-secondary)',
                          }}
                        >
                          (este aparelho)
                        </span>
                      )}
                    </th>
                    <td style={{ padding: 'var(--nev-space-2)' }}>
                      {formatarData(device.last_used_at)}
                    </td>
                    <td style={{ padding: 'var(--nev-space-2)' }}>
                      {formatarData(device.expires_at)}
                    </td>
                    <td style={{ padding: 'var(--nev-space-2)' }}>
                      <Button
                        variant="outline"
                        disabled={!hasReauthentication || submitting}
                        onClick={() => {
                          onRevokeOne(device.id);
                        }}
                      >
                        Revogar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            style={{
              marginTop: 'var(--nev-space-6)',
              paddingTop: 'var(--nev-space-4)',
              borderTop: '1px solid var(--nev-border-subtle)',
            }}
          >
            {confirmandoTudo ? (
              <Alert type="critical" title="Revogar todos os dispositivos?">
                Todos os aparelhos passarão a exigir o código de confirmação, inclusive este. As
                sessões abertas neles serão encerradas.
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--nev-space-3)',
                    marginTop: 'var(--nev-space-3)',
                  }}
                >
                  <Button
                    variant="primary"
                    disabled={submitting}
                    onClick={() => {
                      setConfirmandoTudo(false);
                      onRevokeAll();
                    }}
                  >
                    Sim, revogar todos
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setConfirmandoTudo(false);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </Alert>
            ) : (
              <Button
                variant="outline"
                disabled={!hasReauthentication || submitting}
                onClick={() => {
                  setConfirmandoTudo(true);
                }}
              >
                Revogar todos os dispositivos
              </Button>
            )}
          </div>
        </>
      )}
    </Container>
  );
};
